import { Component, OnDestroy, Optional } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { SapSubmittalService } from '../../../../../services/sap-submittal.service';

/**
 * Confirmation dialog for unlocking a submitted estimate (SAP Resubmittal).
 *
 * Desktop equivalent: SAPReSubmittalConfirmationWindow.xaml / .xaml.cs
 *
 * Flow:
 *   1. Dialog shows explanation text and a textarea for the reason
 *   2. User optionally enters resubmittal notes (max 256 chars) and clicks OK
 *   3. SapSubmittalService.executeUnlockFlow() calls the backend API
 *   4. Backend validates (GOC, collaborative state), creates clone, sets status to Active
 *   5. On success: the returned estimate is reopened through the standard estimate-open flow, then the dialog closes
 *   6. On error: global ErrorInterceptor shows notification, dialog stays open for retry/cancel
 */

/** Desktop: Notes_SAPReSubmittal TextBox MaxLength=256 */
const RESUBMITTAL_NOTES_MAX_LENGTH = 256;
@Component({
  selector: 'app-unlock-submitted-estimate-dialog',
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    TranslatePipe,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule
  ],
  templateUrl: './unlock-submitted-estimate-dialog.component.html',
  styleUrl: './unlock-submitted-estimate-dialog.component.scss'
})
export class UnlockSubmittedEstimateDialogComponent implements OnDestroy {

  /** Resubmittal reason - optional, max 256 chars (desktop: Notes_SAPReSubmittal TextBox MaxLength=256) */
  reason = new FormControl('', [Validators.maxLength(RESUBMITTAL_NOTES_MAX_LENGTH)]);

  /** Exposed for template binding on maxlength attribute */
  readonly maxNotesLength = RESUBMITTAL_NOTES_MAX_LENGTH;

  /** Subject for subscription cleanup */
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Optional() private dialogRef: MatDialogRef<UnlockSubmittedEstimateDialogComponent>,
    private readonly sapSubmittalService: SapSubmittalService
  ) {
  }

  /**
   * Triggered when user clicks OK.
   * Delegates to the service for the full unlock flow; the reason is optional.
   *
   * Desktop equivalent: Yes_Click in SAPReSubmittalConfirmationWindow.xaml.cs
   * (desktop also re-validates collaborative estimate status on OK click — handled server-side in web)
   */
  unlockEstimate(): void {
    if (this.reason.invalid) {
      this.reason.markAsTouched();
      return;
    }

    this.sapSubmittalService.executeUnlockFlow(
      (this.reason.value ?? '').trim(),
      () => this.dialogRef?.close()
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
